import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, AlertTriangle, Zap, Phone, Mail, Share2, Printer, Check, ArrowLeft, CheckCircle2, ArrowRight, Receipt } from 'lucide-react';
import { formatPrice, formatPriceRange, serviceTypeLabel, urgencyColor } from '../../utils/formatters';
import { getCachedPartnerMatch } from '../../utils/partnerLookup';
import PartnerCard from '../partners/PartnerCard';
import { COLORS, RADIUS, SHADOWS } from '../../styles/theme';

export default function ResultsScreen({ result, serviceDetails, companyConfig, embedded, onReset, demoPartner, onShareEmail, onCreateInvoice, showSignupCta = false, userType = null, cardRadius = null }) {
  // Same cardRadius pattern CleaningCalculator.js's own resolvedCardRadius
  // already uses for the service-select step, for the same reason (see
  // its comment): this card used to hardcode RADIUS.xl regardless of what
  // radius the embedding wrapper (Calculator Mode, Widget Mode, etc.)
  // actually used, so the green header's square corners -- clipped to
  // RADIUS.xl by THIS card's own overflow:hidden -- didn't match the
  // outer wrapper's own, different rounded corner one level up. Relying
  // on the outer wrapper's overflow:hidden to force the two into
  // agreement doesn't reliably work once there's a second nested
  // overflow:hidden box with its own solid background in between (this
  // card, and the header div inside it) -- it reads as a visible sliver
  // of the wrapper's background peeking through at the corner, reported
  // 2026-10-29. Explicitly matching the radius here, the same way the
  // service-select header already does, fixes it regardless of ancestor
  // clipping behavior.
  const resolvedCardRadius = cardRadius != null ? cardRadius : RADIUS.xl;
  const [shared, setShared] = useState(false);
  const [partner, setPartner] = useState(null);
  const rootRef = useRef(null);

  // "Email to Coworker" -- only ever passed by the dashboard's "Run an
  // Estimate" tab (see CompanyRunEstimateTab.js), so this button and its
  // inline form only appear there, independent of `embedded` (unlike the
  // copy-a-link Share button below, which is a public-site-only feature and
  // stays gated on !embedded).
  const [shareEmailOpen, setShareEmailOpen] = useState(false);
  const [shareEmailValue, setShareEmailValue] = useState('');
  // Required alongside the email -- without it, a coworker opening this
  // estimate has no way to tell whose job it's for (the subject line and
  // email body both rely on this to say "estimate for <client name>").
  const [shareClientName, setShareClientName] = useState('');
  const [shareEmailStatus, setShareEmailStatus] = useState(''); // '' | 'sending' | 'sent' | 'error'

  const submitShareEmail = async () => {
    if (!shareEmailValue.trim() || !shareClientName.trim() || !onShareEmail || shareEmailStatus === 'sending') return;
    setShareEmailStatus('sending');
    try {
      await onShareEmail(shareEmailValue.trim(), shareClientName.trim());
      setShareEmailStatus('sent');
      setShareEmailValue('');
      setShareClientName('');
      setTimeout(() => { setShareEmailOpen(false); setShareEmailStatus(''); }, 2000);
    } catch {
      setShareEmailStatus('error');
    }
  };

  // Scrolling the window to y=0 assumed the results were at the top of the
  // page, which is true for the standalone /results flow but not for a
  // siteLanding page (CalculatorPage, PartnerDemoPage, etc.) where a hero
  // and other content sit above the embedded calculator -- there, y=0
  // landed back on the hero instead of the results the visitor just got.
  // Scrolls this component's own root into view instead, offset for the
  // sticky navbar (same 70px CleaningCalculator's own step-change scroll
  // uses) so the top of the results isn't tucked underneath it.
  useEffect(() => {
    if (!rootRef.current) return;
    const navbarHeight = 70;
    const top = rootRef.current.getBoundingClientRect().top + window.scrollY - navbarHeight;
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (embedded || demoPartner) return;
    // The lead-capture step (CleaningCalculator's handleLeadNext) already
    // resolves and caches this exact match before the estimate email is
    // sent -- reusing that cache here (instead of a second independent
    // getUserLocation()+findPartner() round trip) is what makes the card
    // appear immediately instead of after its own multi-second geo lookup.
    getCachedPartnerMatch().then(setPartner);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [embedded, demoPartner]);

  if (!result) return null;

  // /partner-demo feeds a sample partner in here to show prospects exactly
  // this card without needing a real geo match -- takes priority over
  // embedded's usual "no partner card" rule and over any real match.
  const effectivePartner = demoPartner || partner;
  const showPartnerCard = !!demoPartner || (!!partner && !embedded);

  const {
    totalLow, totalHigh, suggestedPrice, stateName, stateMultiplier, metroLabel, city,
    recurringAnnualSavings,
    adjustments = [], keyFactors = [], disclaimer, urgencyLevel,
    serviceType, unit, currencySymbol = '$', country,
  } = result;
  // Once a metro-level override actually applied (see METRO_ZIP3_OVERRIDES
  // in defaults.js), stateMultiplier itself already reflects that metro's
  // own figure, not the state's -- but every "market" sentence below still
  // named the whole state, which read as "Texas runs 10% above average"
  // when the real story was Austin specifically, not Texas generally.
  // Falls back to stateName, so a request outside any known metro (the
  // large majority) reads exactly as it always has.
  const marketLabel = metroLabel || stateName;
  // Capitalized variant for the two spots where marketLabel opens a
  // sentence/heading -- metroLabel's "the SF Bay Area"/"the Rio Grande
  // Valley" read fine mid-sentence ("Prices in the SF Bay Area run...")
  // but need a capital T to open "The SF Bay Area market:" or "The SF Bay
  // Area is close to...". A plain state name is already capitalized, so
  // this is a no-op for every request outside a metro override.
  const marketLabelTitle = marketLabel.charAt(0).toUpperCase() + marketLabel.slice(1);
  // GST/HST applies to cleaning services in Canada, but this deliberately
  // doesn't compute a per-province amount -- see cleaningCalculation.js's
  // comment on why. Just a plain heads-up for a CA company's quote.
  // Australia gets the same plain note (also a low, broadly-applicable GST
  // registration threshold -- see defaults.js); the UK deliberately does
  // not, since UK VAT registration is threshold-based and most small
  // cleaning businesses fall under it.
  const showTaxNote = country === 'CA' || country === 'AU';
  const taxNoteText = country === 'AU' ? '+ applicable taxes (GST)' : '+ applicable taxes (GST/HST, varies by province)';

  const primaryColor = companyConfig?.primaryColor || '#2563eb';
  const ctaPhone = companyConfig?.ctaPhone || null;
  const ctaEmail = companyConfig?.ctaEmail || null;
  const companyName = companyConfig?.companyName || null;

  const urgColor = urgencyColor(urgencyLevel);
  const isHighState = stateMultiplier >= 1.15;
  const isLowState = stateMultiplier <= 0.85;

  // Pre-fills the Invoices tab's New Invoice form with this estimate's
  // numbers -- amount defaults to the midpoint (same number the "Midpoint
  // Price" line above already shows), description to the service name, and
  // the "Key factors in your estimate" chips above (home size, bedrooms,
  // condition, frequency, etc.) become the invoice's line items -- the
  // user's ask was specifically to carry those over, pre-filled but still
  // editable/removable/re-addable on the New Invoice form, not the dollar
  // price-breakdown rows (those are already folded into the one total).
  // Client name/email are left blank since Calculator Mode never collects
  // either (it's an internal tool, no lead capture) -- the business fills
  // those in on the Invoices tab itself. estimateSnapshot is kept for
  // reference even though nothing currently displays it back.
  const handleCreateInvoice = () => {
    if (!onCreateInvoice) return;
    onCreateInvoice({
      amount: suggestedPrice != null ? suggestedPrice : Math.round((totalLow + totalHigh) / 2),
      serviceType,
      description: serviceTypeLabel(serviceType, country),
      lineItems: keyFactors.map(f => ({ label: f.label, value: f.impact })),
      estimateSnapshot: { result, serviceDetails },
    });
  };

  const handleShare = async () => {
    const payload = { r: result, d: serviceDetails };
    const encoded = btoa(unescape(encodeURIComponent(JSON.stringify(payload))));
    const url = `${window.location.origin}/results#${encoded}`;
    try {
      await navigator.clipboard.writeText(url);
      setShared(true);
      setTimeout(() => setShared(false), 3000);
    } catch {
      window.prompt('Copy this link:', url);
    }
  };

  const headerBg = urgencyLevel === 'critical'
    ? 'linear-gradient(135deg, #7f1d1d, #dc2626)'
    : urgencyLevel === 'high'
    ? 'linear-gradient(135deg, #78350f, #d97706)'
    : `linear-gradient(135deg, ${primaryColor}, ${primaryColor}cc)`;

  return (
    <>
      {!embedded && (
        <Helmet>
          <title>{serviceTypeLabel(serviceType, country)} Cost Estimate — {stateName} | Clean Estimator</title>
          <meta name="description" content={`Your estimated cost for ${serviceTypeLabel(serviceType, country).toLowerCase()} in ${stateName}: ${formatPriceRange(totalLow, totalHigh, serviceType, currencySymbol)}.`} />
        </Helmet>
      )}

      {/* Bottom padding still applies when embedded, even though top/sides
          don't -- the disclaimer paragraph below the New Estimate button is
          hidden in embedded mode, so without this the button row was the
          very last thing rendered with zero space before the iframe's own
          edge, reading as cut off rather than intentionally placed. */}
      <div ref={rootRef} style={{ padding: embedded ? '0 0 24px' : '40px 16px', background: embedded ? 'white' : COLORS.surfaceMuted, minHeight: embedded ? 'auto' : '100vh' }}>
        <div style={{ maxWidth: 720, margin: '0 auto' }}>

          <div style={{ background: COLORS.surface, borderRadius: resolvedCardRadius, overflow: 'hidden', boxShadow: embedded ? 'none' : SHADOWS.lg, border: embedded ? 'none' : `1px solid ${COLORS.border}`, marginBottom: 20 }}>

            <div style={{ background: headerBg, borderTopLeftRadius: resolvedCardRadius, borderTopRightRadius: resolvedCardRadius, padding: 'clamp(28px, 6vw, 40px) 32px', color: 'white', position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -100, right: -60, width: 320, height: 320, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0) 70%)', pointerEvents: 'none' }} aria-hidden="true" />
              <div style={{ position: 'relative' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700, opacity: 0.85, marginBottom: 14, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                  <CheckCircle2 size={14} /> {serviceTypeLabel(serviceType, country)} Estimate · {city ? `${city}, ${stateName}` : stateName}
                </div>
                {/* A lower clamp() floor (was 34px) plus nowrap -- on a narrow
                    phone a longer range like "$1,345 - $1,632" at 34px had no
                    room left to shrink into and wrapped onto two lines; this
                    lets it keep shrinking down to a size that still fits in
                    one line instead. */}
                <div style={{ fontSize: 'clamp(26px, 8vw, 52px)', fontWeight: 800, lineHeight: 1.1, marginBottom: 8, letterSpacing: '-1px', whiteSpace: 'nowrap' }}>
                  {formatPrice(totalLow, currencySymbol)} – {formatPrice(totalHigh, currencySymbol)}
                  {/* A plain flag, not a computed amount -- see
                      cleaningCalculation.js's comment for why this doesn't
                      try to break out a per-province tax figure. */}
                  {showTaxNote && <span style={{ fontSize: '0.35em', fontWeight: 700, opacity: 0.75, marginLeft: 8, verticalAlign: 'middle' }}>+ tax</span>}
                </div>
                <div style={{ fontSize: 13.5, opacity: 0.85 }}>
                  {unit === 'per_month' ? 'per month' : 'per visit'}
                  {isHighState && ` · ${marketLabel} is a higher-cost market`}
                  {isLowState && ` · ${marketLabel} is a lower-cost market`}
                </div>
                {/* recurringMonthlyLow/High are ALWAYS the exact same numbers as
                    totalLow/High above when a discount applies -- the
                    calculation already folds the discount into the main
                    total (see calculateHomeResidential etc.'s finalLow =
                    recurringLow || range.low). Repeating those same two
                    dollar amounts a second time under a "Recurring" label
                    read as a duplicate/glitch rather than new information.
                    The itemized breakdown below already shows the discount
                    as its own line item with real dollar amounts -- this
                    badge just confirms it applied and adds the one genuinely
                    new number (annual savings), instead of restating the
                    headline. */}
                {recurringAnnualSavings && unit !== 'per_month' && (
                  <div style={{ marginTop: 14, background: 'rgba(255,255,255,0.15)', borderRadius: 8, padding: '10px 14px', display: 'inline-block' }}>
                    <span style={{ fontWeight: 700, fontSize: 13.5 }}>✓ Recurring discount applied</span>
                    <span style={{ opacity: 0.9, fontSize: 13 }}> · Save ~{formatPrice(recurringAnnualSavings, currencySymbol)}/year</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ padding: '24px 20px' }}>
              {disclaimer && (
                <div style={{ background: urgencyLevel === 'critical' ? COLORS.dangerMuted : COLORS.warningMuted, border: `1px solid ${urgencyLevel === 'critical' ? COLORS.dangerBorder : COLORS.warningBorder}`, borderRadius: RADIUS.md, padding: '13px 16px', marginBottom: 20, display: 'flex', gap: 10 }}>
                  <div style={{ flexShrink: 0, marginTop: 1 }}>
                    {urgencyLevel === 'critical' ? <AlertTriangle size={16} color={COLORS.danger} /> : <Zap size={16} color={COLORS.warning} />}
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, color: urgColor, marginBottom: 3, fontSize: 13 }}>
                      {urgencyLevel === 'critical' ? 'Important Notice' : 'Heads Up'}
                    </div>
                    <p style={{ fontSize: 13, color: urgencyLevel === 'critical' ? '#7f1d1d' : '#78350f', lineHeight: 1.6, margin: 0 }}>{disclaimer}</p>
                  </div>
                </div>
              )}

              <div style={{ marginBottom: 20 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Price breakdown</div>
                <div style={{ border: `1px solid ${COLORS.borderSubtle}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
                  {adjustments.filter(adj => !adj.separate).map((adj, i, arr) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10, padding: '10px 12px', borderBottom: i < arr.length - 1 ? `1px solid ${COLORS.borderSubtle}` : 'none', fontSize: 13.5, background: i % 2 === 0 ? COLORS.surface : '#fafafa' }}>
                      {/* flex:1 + minWidth:0 on the label so IT wraps first if
                          the row is tight, instead of the price column
                          (whiteSpace:nowrap, flexShrink:0) breaking apart
                          mid-number onto its own second line. */}
                      <span style={{ flex: 1, minWidth: 0, color: COLORS.ink, textTransform: 'capitalize' }}>{adj.label}</span>
                      <span style={{ flexShrink: 0, whiteSpace: 'nowrap', fontWeight: 600, color: (adj.low < 0 || adj.high < 0) ? COLORS.success : COLORS.ink }}>
                        {adj.low < 0
                          ? `−${formatPrice(Math.abs(adj.low), currencySymbol)} – −${formatPrice(Math.abs(adj.high), currencySymbol)}`
                          : (adj.low === 0 && adj.high === 0 ? 'Included' : formatPriceRange(adj.low, adj.high, null, currencySymbol))}
                      </span>
                    </div>
                  ))}
                  <div style={{ padding: '13px 12px', background: COLORS.surfaceMuted, borderTop: `1.5px solid ${COLORS.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontWeight: 700, fontSize: 15 }}>
                      <span>Estimated total</span>
                      <span style={{ flexShrink: 0, whiteSpace: 'nowrap', color: primaryColor }}>{formatPriceRange(totalLow, totalHigh, null, currencySymbol)}</span>
                    </div>
                    {/* Plain midpoint of the range above, not a smarter
                        per-job recommendation -- its own lighter-weight line
                        directly under "Estimated total", left-aligned like a
                        caption on the row rather than tucked under the price
                        on the right (reported as reading disconnected from
                        the label it's actually describing). Hidden
                        specifically from a homeowner (userType, from
                        UserTypeStep.js -- only ever set on
                        cleanestimator.com's own public pages, see
                        CleaningCalculator.js's askUserType) -- a single
                        anchor number is exactly what a customer would push a
                        real cleaning company down to, the same lowballing
                        risk the range-vs-midpoint distinction already exists
                        to avoid. userType is null everywhere else
                        (Calculator Mode, the embedded widget, demoPartner,
                        BrandingTab's preview), where the viewer either IS
                        the business or was never asked, so the midpoint
                        still shows by default there. */}
                    {suggestedPrice != null && userType !== 'homeowner' && (
                      <div style={{ marginTop: 3, fontSize: 13.5, color: COLORS.ink }}>
                        Midpoint Price @ {formatPrice(suggestedPrice, currencySymbol)}{unit === 'per_month' ? '/mo' : ''}
                      </div>
                    )}
                  </div>
                  {/* Plain flag, not a computed line item -- see
                      cleaningCalculation.js's comment on why this stayed
                      lightweight instead of a computed per-province amount. */}
                  {showTaxNote && (
                    <div style={{ padding: '8px 12px', fontSize: 11.5, color: COLORS.muted, borderTop: `1px solid ${COLORS.borderSubtle}` }}>
                      {taxNoteText}
                    </div>
                  )}
                </div>
                {adjustments.filter(adj => adj.separate).map((adj, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', marginTop: 10, background: COLORS.warningMuted, border: `1px dashed ${COLORS.warningBorder}`, borderRadius: RADIUS.md, fontSize: 13 }}>
                    <span style={{ color: '#78350f' }}>{adj.label}</span>
                    <span style={{ fontWeight: 600, color: '#78350f', whiteSpace: 'nowrap', marginLeft: 12 }}>{formatPriceRange(adj.low, adj.high, null, currencySymbol)}</span>
                  </div>
                ))}
              </div>

              {keyFactors.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 10 }}>Key factors in your estimate</div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    {keyFactors.map((f, i) => (
                      <div key={i} style={{ background: COLORS.surfaceMuted, border: `1px solid ${COLORS.border}`, borderRadius: RADIUS.sm - 1, padding: '6px 12px', fontSize: 12.5 }}>
                        <span style={{ color: COLORS.body }}>{f.label}: </span>
                        <span style={{ fontWeight: 600, color: COLORS.ink, textTransform: 'capitalize' }}>{f.impact}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div style={{ background: COLORS.primaryMuted, borderRadius: RADIUS.md, padding: '13px 16px', marginBottom: 20, fontSize: 13, color: COLORS.primaryHover, display: 'flex', gap: 9, alignItems: 'flex-start' }}>
                <MapPin size={14} style={{ marginTop: 2, flexShrink: 0 }} />
                <span>
                  <strong>{marketLabelTitle} market: </strong>
                  {stateMultiplier > 1.05 && `Prices in ${marketLabel} run ${Math.round((stateMultiplier - 1) * 100)}% above the national average due to higher cost of living and labor.`}
                  {stateMultiplier < 0.95 && `Prices in ${marketLabel} run ${Math.round((1 - stateMultiplier) * 100)}% below the national average, making this a lower-cost market.`}
                  {stateMultiplier >= 0.95 && stateMultiplier <= 1.05 && `${marketLabelTitle} is close to the national average for cleaning service costs.`}
                </span>
              </div>

              {/* showSignupCta (cleanestimator.com's own homepage/landing
                  pages only -- see CleaningCalculator.js's comment) pitches
                  THIS tool itself to the business owner who just saw it work,
                  instead of the partner-referral / "contact a cleaner" cards
                  below -- those are aimed at a homeowner shopping for
                  service, the opposite audience for this specific CTA, so
                  they're suppressed here rather than shown alongside it. */}
              {showSignupCta ? (
                <div style={{ background: `${primaryColor}0d`, border: `1px solid ${primaryColor}28`, borderRadius: RADIUS.md, padding: '18px 20px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <Zap size={16} color={primaryColor} />
                    <span style={{ fontWeight: 700, fontSize: 15, color: COLORS.ink }}>Own a cleaning business?</span>
                  </div>
                  <p style={{ fontSize: 13, color: COLORS.body, margin: '0 0 14px' }}>
                    This is Clean Estimator's internal pricing tool -- the same unlimited, instant calculator, built for your own quoting. 14-day free trial, cancel anytime.
                  </p>
                  <a href="/estimator" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: primaryColor, color: 'white', padding: '11px 22px', borderRadius: RADIUS.sm, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                    Start Free Trial <ArrowRight size={14} />
                  </a>
                </div>
              ) : (
                <>
                  {showPartnerCard && <PartnerCard partner={effectivePartner} />}

                  {/* "Ready to book" / contact-us card only makes sense when a
                      customer is looking at someone else's estimate -- in
                      Calculator Mode (onShareEmail only ever passed there, same
                      signal the Email-to-Coworker button above uses) the viewer
                      IS the business, so there's no one to contact. */}
                  {!showPartnerCard && !onShareEmail && (
                <div style={{ background: embedded && companyName ? `${primaryColor}08` : COLORS.surfaceMuted, border: `1px solid ${embedded && companyName ? primaryColor + '28' : COLORS.border}`, borderRadius: RADIUS.md, padding: '18px 20px' }}>
                  <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.ink, marginBottom: 4 }}>
                    {companyName ? `Ready to book with ${companyName}?` : 'Ready to get real quotes?'}
                  </div>
                  <p style={{ fontSize: 13, color: COLORS.body, marginBottom: 14, margin: '4px 0 14px' }}>
                    {companyName ? 'Contact us for a free, no-obligation on-site quote.' : 'Compare quotes from vetted local cleaning professionals.'}
                  </p>
                  {/* Just the email itself, not a big "Email Us" button --
                      the Call button next to it is a real distinct action
                      (opens the phone dialer), but "Email Us" duplicated
                      that same weight for something that's really just
                      contact info to read and copy. */}
                  {ctaEmail && (
                    <p style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5, color: '#374151', margin: '0 0 12px' }}>
                      <Mail size={14} color={COLORS.body} />
                      <a href={`mailto:${ctaEmail}`} style={{ color: '#374151', textDecoration: 'none' }}>{ctaEmail}</a>
                    </p>
                  )}
                  <div style={{ display: 'flex', gap: 9, flexWrap: 'wrap' }}>
                    {ctaPhone && (
                      <a href={`tel:${ctaPhone}`}
                        style={{ display: 'inline-flex', alignItems: 'center', gap: 7, background: COLORS.success, color: 'white', padding: '11px 22px', borderRadius: RADIUS.sm, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                        <Phone size={14} /> Call {ctaPhone}
                      </a>
                    )}
                    {!ctaEmail && !ctaPhone && (
                      <a href="/" style={{ background: primaryColor, color: 'white', padding: '11px 22px', borderRadius: RADIUS.sm, textDecoration: 'none', fontWeight: 700, fontSize: 14 }}>
                        Contact Us
                      </a>
                    )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={onReset} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.sm, background: COLORS.surface, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, color: '#374151' }}>
              <ArrowLeft size={14} /> New Estimate
            </button>
            {!embedded && (
              <button onClick={handleShare} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.sm, background: COLORS.surface, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, color: '#374151' }}>
                {shared ? <><Check size={14} strokeLinecap="square" strokeLinejoin="miter" /> Copied!</> : <><Share2 size={14} /> Share Results</>}
              </button>
            )}
            {!embedded && (
              <button onClick={() => window.print()} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.sm, background: COLORS.surface, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, color: '#374151' }}>
                <Printer size={14} /> Print
              </button>
            )}
            {onShareEmail && (
              <button onClick={() => setShareEmailOpen(v => !v)} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', border: `1.5px solid ${COLORS.border}`, borderRadius: RADIUS.sm, background: COLORS.surface, cursor: 'pointer', fontWeight: 600, fontSize: 13.5, color: '#374151' }}>
                <Mail size={14} /> Email to Coworker
              </button>
            )}
            {/* Beta-gated (only passed by RunEstimateTab.js's own beta check,
                see docs/invoicing-stripe-connect-plan.md) -- same Calculator
                Mode-only context onShareEmail already uses, not shown on the
                public calculator (an anonymous visitor has nothing to invoice). */}
            {onCreateInvoice && (
              <button onClick={handleCreateInvoice} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 20px', border: 'none', borderRadius: RADIUS.sm, background: primaryColor, color: 'white', cursor: 'pointer', fontWeight: 700, fontSize: 13.5 }}>
                <Receipt size={14} /> Create Invoice
              </button>
            )}
          </div>

          {onShareEmail && shareEmailOpen && (
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', marginTop: 12 }}>
              <input
                type="text"
                value={shareClientName}
                onChange={e => setShareClientName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitShareEmail()}
                placeholder="Client's name"
                style={{ padding: '9px 14px', borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, fontSize: 13.5, minWidth: 160 }}
              />
              <input
                type="email"
                value={shareEmailValue}
                onChange={e => setShareEmailValue(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && submitShareEmail()}
                placeholder="coworker@email.com"
                style={{ padding: '9px 14px', borderRadius: RADIUS.sm, border: `1.5px solid ${COLORS.border}`, fontSize: 13.5, minWidth: 220 }}
              />
              <button
                onClick={submitShareEmail}
                disabled={shareEmailStatus === 'sending' || !shareEmailValue.trim() || !shareClientName.trim()}
                style={{ padding: '9px 18px', borderRadius: RADIUS.sm, border: 'none', background: primaryColor, color: 'white', fontWeight: 700, fontSize: 13.5, cursor: 'pointer', opacity: (shareEmailStatus === 'sending' || !shareEmailValue.trim() || !shareClientName.trim()) ? 0.6 : 1 }}
              >
                {shareEmailStatus === 'sending' ? 'Sending…' : shareEmailStatus === 'sent' ? 'Sent!' : 'Send'}
              </button>
              {shareEmailStatus === 'error' && (
                <span style={{ fontSize: 12.5, color: COLORS.danger, alignSelf: 'center' }}>Failed to send — try again.</span>
              )}
            </div>
          )}

          {!embedded && (
            <p style={{ textAlign: 'center', fontSize: 11.5, color: COLORS.muted, marginTop: 16, maxWidth: 520, margin: '16px auto 0' }}>
              These estimates are for informational purposes only. Actual prices vary.
            </p>
          )}
        </div>
      </div>
    </>
  );
}
